import { Signal, computed, inject } from '@angular/core';

import { ResourcesService } from '@mitsubishi/core';
import { PerformanceFilter, PerformanceSelectOption, PerformanceValue } from '@mitsubishi/shared/data-access';
import { generatePerformanceKey } from '@mitsubishi/utils';

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

type StudyPerformanceFiltersState = {
  performances: Record<string, PerformanceSelectOption[]>;
  appliedFilters: Record<string, Record<string, PerformanceFilter>>;
  subcategoriesFilteredUnitsCount: Record<string, number>;
};

const initialState: StudyPerformanceFiltersState = {
  performances: {},
  appliedFilters: {},
  subcategoriesFilteredUnitsCount: {},
};

export const StudyPerformanceFiltersStore = signalStore(
  withState(initialState),
  withMethods((store, resourcesService = inject(ResourcesService)) => ({
    getSubcategoryPerformances: (subcategoryId: string): Signal<PerformanceSelectOption[]> =>
      computed(() => store.performances()[subcategoryId] ?? []),
    setPerformances: (subcategoryId: string, performances: PerformanceValue[]) => {
      patchState(store, {
        performances: {
          [subcategoryId]: performances.map(
            p =>
              ({
                ...p,
                performanceKey: generatePerformanceKey(p),
                translatedDescription: resourcesService.getTextResourceWithPrefixSuffix({
                  resourceId: p.resourceId,
                  suffixResourceId: p.suffixResourceId,
                  prefixResourceId: p.prefixResourceId,
                }),
              }) as PerformanceSelectOption
          ),
        },
      });
    },
    getSubcategoryPerformanceFiltersSnapshot: (subcategoryId: string) => store.appliedFilters()[subcategoryId] ?? {},
    getSubcategoryPerformanceFilters: (subcategoryId: string) =>
      computed(() => store.appliedFilters()[subcategoryId] ?? {}),
    getSubcategoryPerformanceFilterById: (subcategoryId: string, filterKey: string) =>
      computed(() => store.appliedFilters()[subcategoryId]?.[filterKey]),
    setSubcategoryAppliedPerformanceFilters: (subcategoryId: string, appliedFilters: PerformanceFilter[] | null) => {
      if (!appliedFilters) {
        patchState(store, { appliedFilters: { [subcategoryId]: {} } });
        return;
      }

      const filters = appliedFilters.reduce(
        (acc, f) => {
          const filterKey = generatePerformanceKey(f);
          acc[filterKey] = f;
          return acc;
        },
        {} as Record<string, PerformanceFilter>
      );

      patchState(store, { appliedFilters: { [subcategoryId]: filters } });
    },
    setSubcategoryPerformanceFilter: (subcategoryId: string, filterKey: string, filter: PerformanceFilter | null) => {
      const currentFilters = store.appliedFilters()[subcategoryId] ?? {};
      if (!filter) {
        patchState(store, { appliedFilters: { [subcategoryId]: { ...currentFilters } } });
        return;
      }

      const filterToApply = { [filterKey]: filter };
      patchState(store, { appliedFilters: { [subcategoryId]: { ...currentFilters, ...filterToApply } } });
    },
    removeSubcategoryPerformanceFilter: (subcategoryId: string, filterKey: string) => {
      const currentFilters = store.appliedFilters()[subcategoryId];
      if (!currentFilters) {
        patchState(store, { appliedFilters: { [subcategoryId]: {} } });
        return;
      }

      const updatedFilters = { ...currentFilters };
      delete updatedFilters[filterKey];

      patchState(store, { appliedFilters: { [subcategoryId]: updatedFilters } });
    },
    setSubcategoryFilteredUnitCount: (subcategoryId: string, unitCount: number) => {
      patchState(store, { subcategoriesFilteredUnitsCount: { [subcategoryId]: unitCount } });
    },
  }))
);
